import { useCallback, useEffect, useState } from "react";
import { RefreshCw, ChevronDown, ChevronUp, Download, Inbox, FileText, Search } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { APPLICATION_STATUSES, POOL_WORK_FILTERS, AVAILABILITY_OPTIONS } from "../data/employment";

const STATUS_STYLES = {
  New: "bg-brass/15 text-brass-hover border-brass/40",
  Review: "bg-blue-50 text-blue-800 border-blue-200",
  "Interview Requested": "bg-blue-50 text-blue-800 border-blue-200",
  "Interview Scheduled": "bg-indigo-50 text-indigo-800 border-indigo-200",
  "Awesome Sauce / Strong Candidate": "bg-amber-50 text-amber-800 border-amber-300",
  "Background Check Pending": "bg-orange-50 text-orange-800 border-orange-200",
  "Background Check Cleared": "bg-teal-50 text-teal-800 border-teal-200",
  "Approved On-Call Pool": "bg-forest/10 text-forest border-forest/30",
  "Assignment Offered": "bg-forest/10 text-forest border-forest/30",
  Active: "bg-green-50 text-green-800 border-green-200",
  "Not Selected": "bg-ink/5 text-ink/50 border-ink/15",
  Inactive: "bg-ink/5 text-ink/50 border-ink/15",
};

function resumeUrl(path) {
  const token = localStorage.getItem("macway_admin_token");
  return `${API}/files/${path}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
}

const Detail = ({ label, value }) => (
  <div>
    <dt className="text-xs font-bold uppercase tracking-wider text-ink/50">{label}</dt>
    <dd className="mt-1 text-sm text-ink/80 leading-relaxed">{value || "—"}</dd>
  </div>
);

export const AdminApplicants = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("all"); // all | pool
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [work, setWork] = useState("");
  const [availability, setAvailability] = useState("");
  const [location, setLocation] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [notesDraft, setNotesDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (view === "pool") params.pool = true;
      else if (status) params.status = status;
      if (work) params.work = work;
      if (availability) params.availability = availability;
      if (location.trim()) params.location = location.trim();
      const { data } = await api.get("/applications", { params });
      setApps(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [q, status, work, availability, location, view]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const handleUpdate = async (id, updates) => {
    try {
      await api.patch(`/applications/${id}`, updates);
      setApps((list) => list.map((a) => (a.id === id ? { ...a, ...updates } : a)));
      toast.success("Saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get("/applications/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "macway-applicants.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Permanently delete the application from ${a.first_name} ${a.last_name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/applications/${a.id}`);
      setApps((list) => list.filter((x) => x.id !== a.id));
      setExpanded(null);
      toast.success("Application deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="mt-6" data-testid="applicants-panel">
      {/* Sub-view toggle */}
      <div className="flex flex-wrap gap-2" data-testid="applicants-views">
        <button
          onClick={() => { setView("all"); setStatus(""); }}
          className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider border transition-colors ${view === "all" ? "bg-forest text-ivory border-forest" : "bg-white text-ink/60 border-linen hover:border-forest"}`}
          data-testid="applicants-view-all"
        >
          All Applications
        </button>
        <button
          onClick={() => setView("pool")}
          className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider border transition-colors ${view === "pool" ? "bg-forest text-ivory border-forest" : "bg-white text-ink/60 border-linen hover:border-forest"}`}
          data-testid="applicants-view-pool"
        >
          Approved On-Call Pool
        </button>
      </div>

      {/* Search & filters */}
      <div className="mt-5 bg-white border border-linen p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="applicants-filters">
        <div className="relative">
          <Search className="w-4 h-4 text-ink/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            className="form-input pl-9"
            placeholder="Search name, phone, city…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="applicants-search"
          />
        </div>
        {view === "all" ? (
          <select className="form-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="applicants-status-filter">
            <option value="">All statuses</option>
            {APPLICATION_STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        ) : (
          <div className="hidden lg:block" />
        )}
        <select className="form-input" value={work} onChange={(e) => setWork(e.target.value)} data-testid="applicants-work-filter">
          <option value="">All work types</option>
          {POOL_WORK_FILTERS.map((w) => (
            <option key={w} value={w}>{w}</option>
          ))}
        </select>
        <select className="form-input" value={availability} onChange={(e) => setAvailability(e.target.value)} data-testid="applicants-availability-filter">
          <option value="">Any availability</option>
          {AVAILABILITY_OPTIONS.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <input
          className="form-input"
          placeholder="Location (city or state)"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          data-testid="applicants-location-filter"
        />
      </div>

      <div className="mt-4 flex items-center justify-between">
        <p className="text-sm text-ink/60" data-testid="applicants-count">
          {apps.length} applicant{apps.length === 1 ? "" : "s"}{view === "pool" ? " in the on-call pool" : ""}
        </p>
        <div className="flex items-center gap-3">
          <button onClick={load} className="btn-outline-dark text-xs px-4 py-2.5" data-testid="applicants-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button onClick={handleExport} className="btn-primary text-xs px-4 py-2.5" data-testid="applicants-export">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        {apps.length === 0 && !loading && (
          <div className="bg-white border border-linen p-14 text-center" data-testid="applicants-empty">
            <Inbox className="w-10 h-10 text-ink/30 mx-auto" />
            <p className="mt-4 text-ink/60">No applications match. New applications from the Employment Opportunities page appear here.</p>
          </div>
        )}

        {apps.map((a) => {
          const isOpen = expanded === a.id;
          return (
            <article key={a.id} className="bg-white border border-linen" data-testid={`applicant-row-${a.id}`}>
              <button
                className="w-full px-6 py-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-6 text-left hover:bg-sand/60 transition-colors"
                onClick={() => setExpanded(isOpen ? null : a.id)}
                data-testid={`applicant-toggle-${a.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-ink truncate">{a.first_name} {a.last_name}</p>
                  <p className="text-sm text-ink/60 truncate">{a.city}, {a.state} · {a.work_interests.slice(0, 2).join(", ")}{a.work_interests.length > 2 ? ` +${a.work_interests.length - 2}` : ""}</p>
                </div>
                <div className="text-sm text-ink/60 md:w-36">{a.phone}</div>
                <div className="text-sm text-ink/50 md:w-32">
                  {a.created_at ? new Date(a.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : ""}
                </div>
                <span className={`inline-flex items-center px-3 py-1 text-xs font-bold uppercase tracking-wider border ${STATUS_STYLES[a.status] || STATUS_STYLES.New}`} data-testid={`applicant-status-${a.id}`}>
                  {a.status}
                </span>
                {isOpen ? <ChevronUp className="w-5 h-5 text-ink/40" /> : <ChevronDown className="w-5 h-5 text-ink/40" />}
              </button>

              {isOpen && (
                <div className="border-t border-linen px-6 py-6" data-testid={`applicant-detail-${a.id}`}>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    <Detail label="Email" value={a.email} />
                    <Detail label="Phone" value={a.phone} />
                    <Detail label="City / State" value={`${a.city}, ${a.state}`} />
                    <Detail label="Date applied" value={a.created_at ? new Date(a.created_at).toLocaleString() : ""} />
                    <Detail label="At least 18?" value={a.age_18} />
                    <Detail label="Transportation" value={a.transportation} />
                    <Detail label="Travel range" value={a.travel_range} />
                    <Detail label="Physical work" value={a.physical_work} />
                    <Detail label="Background screening acknowledgment" value={a.background_ack ? "Acknowledged" : "—"} />
                    <div className="sm:col-span-2 lg:col-span-3">
                      <Detail label="Work interests" value={a.work_interests.join(" · ")} />
                    </div>
                    <div className="sm:col-span-2 lg:col-span-3">
                      <Detail label="Experience" value={a.experience.join(" · ")} />
                    </div>
                    {a.experience_notes && (
                      <div className="sm:col-span-2 lg:col-span-3"><Detail label="Experience notes" value={a.experience_notes} /></div>
                    )}
                    <div className="sm:col-span-2 lg:col-span-3">
                      <Detail label="Availability" value={`${a.availability.join(" · ")}${a.availability_notes ? ` — ${a.availability_notes}` : ""}`} />
                    </div>
                    {a.resume && (
                      <div className="sm:col-span-2 lg:col-span-3">
                        <a href={resumeUrl(a.resume.path)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-forest hover:text-brass transition-colors" data-testid={`applicant-resume-${a.id}`}>
                          <FileText className="w-4 h-4" /> View resume ({a.resume.original_filename || "file"})
                        </a>
                      </div>
                    )}
                  </dl>

                  <div className="mt-6 pt-6 border-t border-linen grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor={`status-${a.id}`} className="form-label">Status (internal — never shown to applicants)</label>
                      <select
                        id={`status-${a.id}`}
                        className="form-input"
                        value={a.status}
                        onChange={(e) => handleUpdate(a.id, { status: e.target.value })}
                        data-testid={`applicant-status-select-${a.id}`}
                      >
                        {APPLICATION_STATUSES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor={`notes-${a.id}`} className="form-label">Internal notes (private)</label>
                      <div className="flex gap-2">
                        <input
                          id={`notes-${a.id}`}
                          className="form-input"
                          placeholder="e.g. Great mover, prefers weekends"
                          value={notesDraft[a.id] ?? a.internal_notes ?? ""}
                          onChange={(e) => setNotesDraft((d) => ({ ...d, [a.id]: e.target.value }))}
                          data-testid={`applicant-notes-${a.id}`}
                        />
                        <button
                          onClick={() => handleUpdate(a.id, { internal_notes: notesDraft[a.id] ?? a.internal_notes ?? "" })}
                          className="btn-primary text-xs px-4 py-2.5 shrink-0"
                          data-testid={`applicant-notes-save-${a.id}`}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 text-right">
                    <button
                      onClick={() => handleDelete(a)}
                      className="text-xs font-semibold text-red-700 hover:text-red-900 underline underline-offset-2 transition-colors"
                      data-testid={`applicant-delete-${a.id}`}
                    >
                      Delete this application
                    </button>
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
};
